"use client";

import type { PaymentProfileDto } from "@sticker-giant/sg-frontend-common";
import { useQuery } from "@tanstack/react-query";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";
import { useServicesStore } from "@/stores/useServicesStore";

export const PAYMENT_PROFILES_STALE_TIME = 300_000;

export function paymentProfilesQueryKey(customerId: string | undefined) {
  return ["paymentProfiles", customerId] as const;
}

export function usePaymentProfiles() {
  const paymentClient = useServicesStore(state => state.paymentClient);
  const customerId = useAuthClaimsStore(state => state.claims?.sg_customer_id);

  // Handle users with unverified emails
  const canRead = useAuthClaimsStore(state => !!state.claims?.permissions?.includes("read:payments"));

  return useQuery({
    queryKey: paymentProfilesQueryKey(customerId),
    queryFn: async (): Promise<PaymentProfileDto[]> => {
      if (!paymentClient || !customerId) return [];

      const response = await paymentClient.getPaymentProfilesByCustomer(customerId);
      return response.content ?? [];
    },
    enabled: !!paymentClient && !!customerId && canRead,
    staleTime: PAYMENT_PROFILES_STALE_TIME,
    refetchOnWindowFocus: false,
    // A failed load stays failed until refresh: PaymentMethodRadio's loading gate remounts the
    // radio's observers, and a retry-on-mount would flip back to loading in an endless skeleton loop.
    retryOnMount: false,
  });
}
